import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiX, FiCheck, FiSettings, FiLoader, FiList, FiArrowLeft, FiInfo } from 'react-icons/fi';
import { ToastContainer, toast } from 'react-toastify';
import api from '../../api'
import 'react-toastify/dist/ReactToastify.css';

const API_URL = '/modules';

// Composant affichant le détail d'un module via les données reçues
const ModuleDetail = ({ moduleData, onClose }) => {
  // Extraire organisations uniques depuis moduleData.organisations
  const organisations = Array.isArray(moduleData.organisations) ? moduleData.organisations : [];
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Vérifier la taille de l'écran
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 md:p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    >
      <motion.div
        initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-gray-200 flex flex-col"
      >
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900">
            Organisations du module : {moduleData.nom}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <FiX size={20} />
          </button>
        </div>
        
        <div className="px-4 py-2 bg-gray-50 flex items-center text-sm text-gray-600">
          <FiList className="mr-1" /> Nombre : {organisations.length}
        </div>
        
        <div className="overflow-auto flex-grow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="px-3 py-2 md:px-4 md:py-2 text-left text-xs font-medium uppercase">Nom</th>
                {!isMobile && (
                  <>
                    <th className="px-3 py-2 md:px-4 md:py-2 text-left text-xs font-medium uppercase">Description</th>
                    <th className="px-3 py-2 md:px-4 md:py-2 text-left text-xs font-medium uppercase">Adresse</th>
                    <th className="px-3 py-2 md:px-4 md:py-2 text-left text-xs font-medium uppercase">Téléphone</th>
                    <th className="px-3 py-2 md:px-4 md:py-2 text-left text-xs font-medium uppercase">Domaine Auth</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {organisations.map((o) => (
                <tr key={o.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 md:px-4 md:py-2 text-sm text-gray-900">
                    <div className="font-medium">{o.nomOrganisation}</div>
                    {isMobile && (
                      <div className="text-xs text-gray-500 mt-1">
                        <div>{o.description && `${o.description.substring(0, 30)}...`}</div>
                        <div>{o.domaine_auth && `Domaine: ${o.domaine_auth}`}</div>
                      </div>
                    )}
                  </td>
                  {!isMobile && (
                    <>
                      <td className="px-3 py-2 md:px-4 md:py-2 text-sm text-gray-700">{o.description}</td>
                      <td className="px-3 py-2 md:px-4 md:py-2 text-sm text-gray-700">{o.adresse}</td>
                      <td className="px-3 py-2 md:px-4 md:py-2 text-sm text-gray-700">{o.telephone}</td>
                      <td className="px-3 py-2 md:px-4 md:py-2 text-sm text-gray-700">{o.domaine_auth}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {isMobile && organisations.length > 0 && (
          <div className="p-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 text-center">
            <FiInfo className="inline mr-1" /> Faites défiler pour voir plus d'informations
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

const ParametraModules = () => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ nom: '' });
  const [selectedModule, setSelectedModule] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Vérifier la taille de l'écran
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchModules = async () => {
    setLoading(true);
    try {
      const res = await api.get(API_URL);
      const payload = res.data.data ?? res.data;
      setModules(payload);
    } catch (err) {
      toast.error('Erreur chargement modules');
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchModules(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(API_URL, formData);
      toast.success('Module ajouté');
      fetchModules();
      setShowForm(false);
      setFormData({ nom: '' });
    } catch (err) {
      toast.error('Erreur enregistrement');
      setError(err.message);
    } finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <FiLoader className="animate-spin text-4xl text-gray-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 min-h-screen bg-gray-50">
      <ToastContainer />
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 md:mb-8 gap-4">
          <h1 className="text-xl md:text-2xl font-bold flex items-center text-gray-900">
            <FiSettings className="mr-2 md:mr-3 text-gray-700" /> Modules
          </h1>
          <button 
            onClick={() => setShowForm(true)} 
            className="flex items-center px-3 py-2 md:px-4 md:py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900 transition w-full md:w-auto justify-center"
          >
            <FiPlus className="mr-2" /> Ajouter
          </button>
        </div>

        <div className="bg-white rounded-lg shadow border overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-800 text-white">
              <tr>
                <th className="px-4 py-3 md:px-6 md:py-3 text-left text-xs font-medium uppercase">Nom</th>
                <th className="px-4 py-3 md:px-6 md:py-3 text-center text-xs font-medium uppercase">Organisations</th>
                {!isMobile && (
                  <th className="px-4 py-3 md:px-6 md:py-3 text-center text-xs font-medium uppercase">Créé le</th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {modules.map((mod) => (
                <tr 
                  key={mod.id} 
                  onClick={() => setSelectedModule(mod)} 
                  className="cursor-pointer hover:bg-gray-50 transition"
                >
                  <td className="px-4 py-4 md:px-6 md:py-4 text-sm text-gray-900">
                    <div className="font-medium">{mod.nom}</div>
                    {isMobile && (
                      <div className="text-xs text-gray-500 mt-1">
                        Créé le {new Date(mod.created_at).toLocaleDateString('fr-FR')}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 md:px-6 md:py-4 text-sm text-gray-700 text-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <FiList className="mr-1" />{(mod.organisations || []).length}
                    </span>
                  </td>
                  {!isMobile && (
                    <td className="px-4 py-4 md:px-6 md:py-4 text-sm text-gray-700 text-center">
                      {new Date(mod.created_at).toLocaleDateString('fr-FR')}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <AnimatePresence>
          {showForm && (
            <motion.div 
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 md:p-4" 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }}
            >
              <motion.div 
                initial={{ scale: 0.95 }} 
                animate={{ scale: 1 }} 
                exit={{ scale: 0.95 }} 
                className="bg-white rounded-lg shadow-xl w-full max-w-md border border-gray-200 p-4 md:p-6 relative"
              >
                <button 
                  onClick={() => setShowForm(false)} 
                  className="absolute top-3 right-3 md:top-4 md:right-4 text-gray-500 hover:text-gray-700"
                >
                  <FiX size={20} />
                </button>
                <h2 className="text-lg md:text-xl font-semibold mb-4 text-gray-900">Ajouter un module</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-800">Nom</label>
                    <input 
                      type="text" 
                      required 
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-500" 
                      value={formData.nom} 
                      onChange={(e) => setFormData({ nom: e.target.value })} 
                    />
                  </div>
                  <div className="flex flex-col-reverse md:flex-row justify-end gap-3 mt-6">
                    <button 
                      type="button" 
                      onClick={() => setShowForm(false)} 
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 mt-2 md:mt-0"
                    >
                      Annuler
                    </button>
                    <button 
                      type="submit" 
                      className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900 flex items-center justify-center"
                    >
                      <FiCheck className="mr-2" />Enregistrer
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}

          {selectedModule && <ModuleDetail moduleData={selectedModule} onClose={() => setSelectedModule(null)} />}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ParametraModules;